import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Observable, Subject, BehaviorSubject, of } from 'rxjs';
import { takeUntil, startWith, map, switchMap, catchError, tap, finalize, distinctUntilChanged } from 'rxjs/operators';

import { CereriService } from '../../cereri.service';
import { NotificationService } from 'src/app/services/notification.service';
import { DocumentGenerabil } from '../../cereri.interface';
import { Generic } from 'src/app/interfaces/generic.interface';
import { CommonService } from 'src/app/services/common.service';
import { notDefaultValidator } from 'src/app/validators/not-default';

export interface DocumentAssignmentDialogData {
    document: DocumentGenerabil;
    cerereId: number;
    directii: any[];
}

export interface AvailableOperator {
    id: number;
    functie_id: number;
    directie_id: number;
    compartiment_id: number | null;
    user_id: number | null;
    functie: { id: number; name: string };
    directie: { id_directie: number; name: string };
    compartiment?: { id_compartiment: number; name: string };
    user?: { id_user: number; full_name: string };
}

@Component({
    selector: 'app-document-assignment',
    templateUrl: './document-assignment.component.html',
    styleUrls: ['./document-assignment.component.scss']
})
export class DocumentAssignmentComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();

    assignmentForm!: FormGroup;
    isLoading = false;
    loading = false;
    isSaving = false;

    directiiActive: Generic[] = this.data.directii;
    compartimenteActive!: Generic[];
    usersActive!: Generic[];
    functii!: Generic[];

    // Filtered fields for search
    filteredDirectii: Generic[] = this.data.directii;
    directiiControl = new FormControl();

    filteredCompartimente: any[] = [];
    compartimenteControl = new FormControl();

    filteredUsers: any[] = [];
    usersControl = new FormControl();

    filteredFunctii: any[] = [];
    functiiControl = new FormControl();

    availableOperators$ = of([]) as Observable<AvailableOperator[]>;
    availableFunctii$ = of([]) as Observable<any[]>;

    selectedOperator$ = new BehaviorSubject<AvailableOperator | null>(null);
    assignmentMode: 'existing' | 'custom' = 'existing';

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: DocumentAssignmentDialogData,
        private dialogRef: MatDialogRef<DocumentAssignmentComponent>,
        private fb: FormBuilder,
        private cereriService: CereriService,
        private notificationService: NotificationService,
        private commonService: CommonService
    ) {
        this.initForm();
    }

    ngOnInit(): void {
        this.loadAvailableOperators();
        this.loadFunctii();
        this.setupFormSubscriptions();
        this.loadCurrentAssignment();
        this.renderFieldsSearch();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    renderFieldsSearch() {
        this.directiiControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredDirectii = this.directiiActive;
                    return of(this.directiiActive);
                }
                return this.filterDirectii(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredDirectii = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.compartimenteControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredCompartimente = this.compartimenteActive;
                    return of(this.compartimenteActive);
                }
                return this.filterCompartimente(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredCompartimente = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.usersControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredUsers = this.usersActive;
                    return of(this.usersActive);
                }
                return this.filterUsers(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredUsers = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.functiiControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredFunctii = this.functii;
                    return of(this.functii);
                }
                return this.filterFunctii(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredFunctii = data;
            }),
            catchError(() => of([]))
        ).subscribe();
    }

    private initForm(): void {
        this.assignmentForm = this.fb.group({
            assignment_mode: ['existing'],
            selected_operator_id: [-1],
            assigned_directie_id: [-1, [Validators.required, notDefaultValidator('-1')]],
            assigned_compartiment_id: [-1],
            assigned_functie_id: [-1, [Validators.required, notDefaultValidator('-1')]],
            assigned_user_id: [-1],
            assignment_notes: ['']
        });
    }

    private setupFormSubscriptions(): void {
        // Watch assignment mode changes
        this.assignmentForm.get('assignment_mode')?.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(mode => {
                this.assignmentMode = mode;
                // if (mode === 'existing') {
                //     // this.resetCustomFields();
                // } else {
                //     this.resetOperatorSelection();
                //     console.log(this.assignmentForm.get('assigned_directie_id')?.value);
                // }
            });

        // Watch directie changes for custom mode
        // this.assignmentForm.get('assigned_directie_id')?.valueChanges
        //     .pipe(
        //         takeUntil(this.destroy$),
        //         switchMap(directieId => {
        //             if (directieId && this.assignmentMode === 'custom') {
        //                 return this.cereriService.getCompartimenteByDirectieForCerere(directieId)
        //                     .pipe(
        //                         map((response: any) => response.data || response || []),
        //                         catchError(() => of([]))
        //                     );
        //             }
        //             return of([]);
        //         })
        //     )
        //     .subscribe(compartimente => {
        //         this.availableCompartimente$ = of(compartimente);
        //         // Reset dependent fields
        //         this.assignmentForm.patchValue({
        //             assigned_compartiment_id: null,
        //             assigned_user_id: null
        //         });
        //     });

        // // Watch compartiment changes for custom mode
        // this.assignmentForm.get('assigned_compartiment_id')?.valueChanges
        //     .pipe(
        //         takeUntil(this.destroy$),
        //         switchMap(compartimentId => {
        //             const directieId = this.assignmentForm.get('assigned_directie_id')?.value;
        //             if (directieId && this.assignmentMode === 'custom') {
        //                 return this.cereriService.getAvailableUsers(directieId, compartimentId)
        //                     .pipe(
        //                         map((response: any) => response.data || response || []),
        //                         catchError(() => of([]))
        //                     );
        //             }
        //             return of([]);
        //         })
        //     )
        //     .subscribe(users => {
        //         this.availableUsers$ = of(users);
        //         this.assignmentForm.patchValue({ assigned_user_id: null });
        //     });
    }

    private loadAvailableOperators(): void {
        this.isLoading = true;

        // Subscribe immediately to trigger the HTTP call
        this.cereriService.getAvailableOperatorsForDocument(this.data.document.id_document)
            .pipe(
                takeUntil(this.destroy$),
                catchError(error => {
                    console.error('Error loading available operators:', error);
                    this.isLoading = false;
                    this.notificationService.warningSwal('Eroare', 'Nu s-au putut încărca operatorii disponibili', 'error');
                    return of([]);
                })
            )
            .subscribe({
                next: (operators) => {
                    this.isLoading = false;
                    // Set the observable with the loaded data
                    this.availableOperators$ = of(operators);
                },
                error: (error) => {
                    console.error('Subscription error:', error);
                    this.isLoading = false;
                }
            });
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    filterDirectii(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.directiiActive.filter(directie =>
            this.normalizeString(directie.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onDirectieSelectClosed() {
        const selectedValue = this.directiiControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredDirectii.some(directie =>
                directie.id === this.assignmentForm.get('assigned_directie_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.directiiControl.setValue(null);
                this.filteredDirectii = this.directiiActive;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onDirectieChange(directieId: number): void {
        this.assignmentForm.controls['assigned_compartiment_id'].patchValue(-1);
        this.assignmentForm.controls['assigned_user_id'].patchValue(-1);

        if (directieId == -1) {
            this.compartimenteActive = [];
            this.filteredCompartimente = [];
            this.usersActive = [];
            this.filteredUsers = [];
            return;
        }

        if (directieId) {
            this.loadCompartimenteActive(directieId);
            this.loadUsers();
        }
    }

    loadCompartimenteActive(directieId?: number) {
        this.loading = true;
        let compartimenteObservable$ = directieId
            ? this.cereriService.getCompartimenteByDirectieForCerere(directieId)
            : this.commonService.getCompartimenteActive();

        compartimenteObservable$.pipe(
            map((results: any) => [...results.data]),
            finalize(() => (this.loading = false))
        ).subscribe({
            next: (res) => {
                this.compartimenteActive = [...res];
                this.filteredCompartimente = [...res];
            },
            error: (err) => {
                console.log(err);
                this.filteredCompartimente = [];
            },
        })
    }

    filterCompartimente(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.compartimenteActive.filter(compartiment =>
            this.normalizeString(compartiment.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onCompartimentSelectClosed() {
        const selectedValue = this.compartimenteControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredCompartimente.some(compartiment =>
                compartiment.id === this.assignmentForm.get('assigned_compartiment_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.compartimenteControl.setValue(null);
                this.filteredCompartimente = this.compartimenteActive;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onCompartimentChange(compartimentId: number) {
        this.assignmentForm.controls['assigned_user_id'].patchValue(-1);

        if (compartimentId) {
            this.loadUsers();
        }
    }

    loadUsers() {
        this.loading = true;
        const directieId = this.assignmentForm.get('assigned_directie_id')?.value;
        const compartimentId = this.assignmentForm.get('assigned_compartiment_id')?.value !== -1 ? this.assignmentForm.get('assigned_compartiment_id')?.value : undefined;

        this.cereriService.getAvailableUsers(directieId, compartimentId)
            .pipe(
                map((results: any) => [...results.data]),
                finalize(() => (this.loading = false))
            ).subscribe({
                next: (res) => {
                    this.usersActive = [...res];
                    this.filteredUsers = [...res];
                },
                error: (err) => {
                    console.log(err);
                    this.filteredUsers = [];
                },
            });
    }

    filterUsers(searchTerm: string): Observable<Generic[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        return of(this.usersActive.filter(user =>
            this.normalizeString(user.name).includes(normalizedSearchTerm)
        ));
    }

    onUsersSelectClosed() {
        const selectedValue = this.usersControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredUsers.some(user =>
                user.id === this.assignmentForm.get('assigned_user_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.usersControl.setValue(null);
                this.filteredUsers = this.usersActive;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    private loadFunctii(): void {
        this.loading = true;
        this.cereriService.getAvailableFunctii()
            .pipe(
                map((response: any) => response.data || []),
                finalize(() => this.loading = false)
            )
            .subscribe({
                next: (res) => {
                    this.functii = res;
                    this.filteredFunctii = res;
                },
                error: (error) => {
                    console.error('Error loading functii:', error);
                }
            })
    }

    filterFunctii(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.functii.filter(functie =>
            this.normalizeString(functie.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onFunctieSelectClosed() {
        const selectedValue = this.functiiControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredFunctii.some(functie =>
                functie.id === this.assignmentForm.get('assigned_functie_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.functiiControl.setValue(null);
                this.filteredFunctii = this.functii;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    private loadCurrentAssignment(): void {
        if (this.data.document.has_assignment) {
            this.cereriService.getDocumentAssignmentDetails(this.data.document.id_document)
                .pipe(takeUntil(this.destroy$))
                .subscribe(assignmentDetails => {
                    if (assignmentDetails) {
                        this.assignmentForm.patchValue({
                            assigned_directie_id: assignmentDetails.data.assigned_directie_id,
                            assigned_compartiment_id: assignmentDetails.data.assigned_compartiment_id || -1,
                            assigned_functie_id: assignmentDetails.data.assigned_functie_id,
                            assigned_user_id: assignmentDetails.data.assigned_user_id || -1,
                            assignment_notes: assignmentDetails.data.assignment_notes
                        });

                        if (assignmentDetails.data.assigned_directie_id) {
                            this.loadCompartimenteActive(assignmentDetails.data.assigned_directie_id)
                        }

                        this.loadUsers();

                        // Set mode based on assignment type
                        if (assignmentDetails.data.assignment_type === 'manual') {
                            this.assignmentForm.patchValue({ assignment_mode: 'custom' });
                            this.assignmentMode = 'custom';
                        }
                    }
                });
        }
    }

    onOperatorSelect(): void {
        const selectedId = this.assignmentForm.get('selected_operator_id')?.value;
        if (selectedId) {
            this.availableOperators$.subscribe(operators => {
                const selected = operators.find(op => op.id === selectedId);
                if (selected) {
                    this.selectedOperator$.next(selected);
                }
            });
        } else {
            this.selectedOperator$.next(null);
        }
    }

    private resetCustomFields(): void {
        this.assignmentForm.patchValue({
            assigned_directie_id: null,
            assigned_compartiment_id: null,
            assigned_functie_id: null,
            assigned_user_id: null
        });
    }

    private resetOperatorSelection(): void {
        this.assignmentForm.patchValue({
            selected_operator_id: null
        });
        this.selectedOperator$.next(null);
    }

    onSubmit(): void {
        if (this.assignmentForm.invalid) {
            return;
        }

        this.isSaving = true;
        const formData = this.assignmentForm.value;

        let assignmentData: any = {
            assignment_notes: formData.assignment_notes
        };

        if (this.assignmentMode === 'existing' && formData.selected_operator_id) {
            // Use selected operator data
            this.availableOperators$.subscribe(operators => {
                const selected = operators.find(op => op.id === formData.selected_operator_id);
                if (selected) {
                    assignmentData = {
                        ...assignmentData,
                        assigned_directie_id: selected.directie_id,
                        assigned_compartiment_id: selected.compartiment_id,
                        assigned_functie_id: selected.functie_id,
                        assigned_user_id: selected.user_id
                    };
                }
            });
        } else if (this.assignmentMode === 'custom') {
            // Use custom selection
            assignmentData = {
                ...assignmentData,
                assigned_directie_id: formData.assigned_directie_id,
                assigned_compartiment_id: formData.assigned_compartiment_id,
                assigned_functie_id: formData.assigned_functie_id,
                assigned_user_id: formData.assigned_user_id
            };
        }

        const apiCall = this.data.document.has_assignment
            ? this.cereriService.updateDocumentAssignment(this.data.document.id_document, assignmentData)
            : this.cereriService.assignOperatorToDocument(this.data.document.id_document, assignmentData);

        apiCall.pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    this.isSaving = false;
                    this.notificationService.successSwal(
                        'Succes',
                        'Asignarea documentului a fost salvată cu succes'
                    );
                    this.dialogRef.close({ success: true, data: response });
                },
                error: (error) => {
                    this.isSaving = false;
                    console.error('Error saving document assignment:', error);
                    this.notificationService.warningSwal(
                        'Eroare',
                        error.error?.message || 'A apărut o eroare la salvarea asignării',
                        'error'
                    );
                }
            });
    }

    onUnassign(): void {
        if (!this.data.document.has_assignment) {
            return;
        }

        this.notificationService.confirmSwal(
            'Confirmare',
            'Sunteți sigur că doriți să eliminați asignarea acestui document?',
        ).then((result: any) => {
            if (result.isConfirmed) {
                this.isSaving = true;
                this.cereriService.unassignDocumentOperator(this.data.document.id_document)
                    .pipe(takeUntil(this.destroy$))
                    .subscribe({
                        next: () => {
                            this.isSaving = false;
                            this.notificationService.successSwal(
                                'Succes',
                                'Asignarea documentului a fost eliminată cu succes'
                            );
                            this.dialogRef.close({ success: true, unassigned: true });
                        },
                        error: (error) => {
                            this.isSaving = false;
                            console.error('Error unassigning document:', error);
                            this.notificationService.warningSwal(
                                'Eroare',
                                'A apărut o eroare la eliminarea asignării',
                                'error'
                            );
                        }
                    });
            }
        });
    }

    onCancel(): void {
        this.dialogRef.close({ success: false });
    }
}